import { type ActiveFilter } from 'shared/components/Filter'
import { type Filter } from 'shared/api/analytics'
import {
  DIMENSION_OPERATORS_WITH_TYPES,
  type DimensionAndOptionOperatorType,
  type MetricOperatorType
} from 'shared/constants/filterOperators'

const METRIC_OPERATOR_TO_CIRRUS: Record<MetricOperatorType, string> = {
  eq: '=',
  gt: '>',
  lt: '<',
  gte: '>=',
  lte: '<='
}

const DIMENSION_OPERATOR_TO_CIRRUS: Record<DimensionAndOptionOperatorType, string> = {
  is: '=',
  not_is: '!=',
  one_of: 'IN',
  not_one_of: 'NOT_IN',
  equals: '=',
  not_equals: '!=',
  contains: 'LIKE',
  not_contains: 'NOT_LIKE',
  starts_with: 'LIKE',
  not_starts_with: 'NOT_LIKE',
  ends_with: 'LIKE',
  not_ends_with: 'NOT_LIKE',
  matches_regex: 'REGEXP_CONTAINS',
  not_matches_regex: 'NOT_REGEXP_CONTAINS',
  is_null: 'IS_NULL',
  is_not_null: 'IS_NOT_NULL'
}

const getScalarValue = (data: ActiveFilter['data']): string | null => {
  if (data == null) {
    return null
  }
  if (typeof data === 'string') {
    return data
  }
  if (Array.isArray(data)) {
    return null
  }
  return data.value
}

const isNumericScalar = (value: string): boolean => {
  if (value.trim() === '') {
    return false
  }
  return Number.isFinite(Number(value))
}

export const transformSingleActiveFilterToCirrusAPIFormat = (filter: ActiveFilter): Filter => {
  const dimensionOperator = DIMENSION_OPERATOR_TO_CIRRUS[filter.operator as DimensionAndOptionOperatorType]

  if (
    filter.operator === DIMENSION_OPERATORS_WITH_TYPES.is_null.id ||
    filter.operator === DIMENSION_OPERATORS_WITH_TYPES.is_not_null.id
  ) {
    return {
      field: filter.parameter.value,
      operator: dimensionOperator
    }
  }

  let filterValue = filter.data
  const metricOperator = METRIC_OPERATOR_TO_CIRRUS[filter.operator as MetricOperatorType]

  if (typeof filterValue === 'string' || !Array.isArray(filterValue)) {
    const scalar = getScalarValue(filterValue)

    if (metricOperator != null && scalar != null) {
      return {
        field: filter.parameter.value,
        operator: metricOperator,
        value: scalar
      }
    }

    switch (filter.operator) {
      case DIMENSION_OPERATORS_WITH_TYPES.contains.id:
        filterValue = `%${scalar ?? ''}%`
        break
      case DIMENSION_OPERATORS_WITH_TYPES.starts_with.id:
        filterValue = `${scalar ?? ''}%`
        break
      case DIMENSION_OPERATORS_WITH_TYPES.ends_with.id:
        filterValue = `%${scalar ?? ''}`
        break
      case DIMENSION_OPERATORS_WITH_TYPES.not_contains.id:
        filterValue = `%${scalar ?? ''}%`
        break
      case DIMENSION_OPERATORS_WITH_TYPES.not_starts_with.id:
        filterValue = `${scalar ?? ''}%`
        break
      case DIMENSION_OPERATORS_WITH_TYPES.not_ends_with.id:
        filterValue = `%${scalar ?? ''}`
        break
    }

    let operator = dimensionOperator

    if (operator == null) {
      if (scalar == null) {
        return {
          field: filter.parameter.value,
          operator: 'IS_NULL'
        }
      }
      if (isNumericScalar(scalar)) {
        return {
          field: filter.parameter.value,
          operator: '=',
          value: scalar
        }
      }
      filterValue = `%${scalar}%`
      operator = 'LIKE'
    }

    return {
      field: filter.parameter.value,
      operator,
      value: typeof filterValue === 'string' ? filterValue : scalar ?? ''
    }
  }

  return {
    field: filter.parameter.value,
    operator: dimensionOperator,
    value: filterValue.map((option) => option.value)
  }
}

export const transformActiveFiltersToCirrusAPIFormat = (filters: ActiveFilter[]): Filter[] => {
  const cirrusFilters = filters.map(transformSingleActiveFilterToCirrusAPIFormat)

  return cirrusFilters
}
